import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useHomeLocation from '../../hooks/useHomeLocation';
import useCurrentLocation from '../../hooks/useCurrentLocation';
import useGeolocateCity from '../../hooks/useGeolocateCity';
import Icon from '../shared/Icon';
import CloseButton from '../shared/CloseButton';
import Spinner from '../shared/Spinner';

// Dismissal is per-user and temporary: the card comes back after a week. A
// permanent dismissal would recreate the problem this card exists to fix —
// the /welcome skip is one-shot, and without a home the feed can only serve
// the global spray.
const RESURFACE_DAYS = 7;
const dismissKey = (userId) => `locationNudge:dismissed:${userId}`;

const isDismissed = (userId) => {
  if (!userId) return false;
  try {
    const at = Number(localStorage.getItem(dismissKey(userId)));
    return !!at && Date.now() - at < RESURFACE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

/**
 * LocationNudgeCard — "set your location" prompt for users with no home.
 *
 * Renders on Home and the For You feed when the account has no explicit home
 * location (a timezone-inferred seed doesn't count — that's provisional by
 * design) and no live geolocation fix. Instead of silently serving
 * globally-popular content that *looks* local, the feed asks for the one
 * thing that would make it actually local.
 *
 * One-tap flow: browser geolocation → local reverse geocode → save as home
 * via the same setHome funnel /welcome uses (so coords land synchronously and
 * the settingsUpdated event refreshes every feed surface). Falls back to a
 * Settings deep link (?settings=profile) when geolocation is declined.
 */
function LocationNudgeCard({ onHomeSet, required = false }) {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { home, isSet, loading, setHome } = useHomeLocation();
  const liveCoords = useCurrentLocation();
  const { locate, locating, error: geoError } = useGeolocateCity();

  const [dismissed, setDismissed] = useState(() => isDismissed(currentUser?.id));
  const [savedCity, setSavedCity] = useState(null);
  const [saveFailed, setSaveFailed] = useState(false);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      if (currentUser?.id) localStorage.setItem(dismissKey(currentUser.id), String(Date.now()));
    } catch { /* private mode — session-only dismissal */ }
  }, [currentUser]);

  const useCurrent = useCallback(async () => {
    setSaveFailed(false);
    const city = await locate();
    if (!city) return; // geoError carries the failure kind
    const result = await setHome({
      name: city.name,
      coordinates: city.coordinates,
      placeId: null,
      countryCode: city.countryCode || null
    });
    if (result?.saved === false) {
      setSaveFailed(true);
      return;
    }
    setSavedCity(city.name);
    if (onHomeSet) onHomeSet(city);
  }, [locate, setHome, onHomeSet]);

  // Hidden while home resolves (no flash), once a home exists, when live
  // geolocation already anchors the feed, or within the dismissal window.
  // Exception: keep showing the success state after this mount saved a home.
  // `required` (no discovery center) only overrides dismissal when the user
  // truly has no home: a saved home whose geocode failed must not get an
  // undismissable card asking for a location they already gave.
  const mustShow = required && !loading && !isSet && !home && !liveCoords;
  if (!savedCity && !mustShow && (loading || isSet || !!home || liveCoords || dismissed)) return null;

  if (savedCity) {
    return (
      <div className="bg-surface border border-subtle rounded-card p-4 sm:p-5 flex items-start gap-3">
        <Icon name="checkCircle" className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-semibold text-content-primary text-sm sm:text-base">
            {t('locationNudge.successTitle', { city: savedCity })}
          </p>
          <p className="text-sm text-content-secondary mt-1">
            {t('locationNudge.successBody')}{' '}
            <button
              type="button"
              onClick={() => navigate('?settings=profile&section=location')}
              className="text-primary hover:text-primary-dark underline transition-colors"
            >
              {t('locationNudge.edit')}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-subtle rounded-card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <Icon name="mapPin" className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold text-content-primary text-sm sm:text-base">
              {t('locationNudge.title')}
            </p>
            <p className="text-sm text-content-secondary mt-1">
              {t('locationNudge.body')}
            </p>
          </div>
        </div>
        {!mustShow && <CloseButton
          variant="plain"
          size="sm"
          onClick={dismiss}
          ariaLabel={t('locationNudge.dismiss')}
          className="-m-2"
        />}
      </div>

      {(geoError || saveFailed) && (
        <p className="text-sm text-error mt-3" role="alert">
          {saveFailed
            ? t('locationNudge.saveFailed')
            : t(`locationNudge.geoError.${geoError}`)}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button
          type="button"
          onClick={useCurrent}
          disabled={locating}
          className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-primary hover:bg-primary-dark text-content-inverse rounded-lg font-semibold text-sm transition-all duration-fast active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
        >
          {locating ? <Spinner size="xs" variant="inverse" /> : <Icon name="crosshairs" className="w-4 h-4" />}
          {locating ? t('locationNudge.locating') : t('locationNudge.useCurrent')}
        </button>
        <button
          type="button"
          onClick={() => navigate('?settings=profile&section=location')}
          className="text-sm text-content-secondary hover:text-content-primary underline transition-colors min-h-[44px]"
        >
          {t('locationNudge.typeInstead')}
        </button>
      </div>
    </div>
  );
}

export default LocationNudgeCard;
